import React, { useContext, useEffect } from 'react';
import { TreeItem } from '.';
import TreeNodeItem from './TreeNodeItem';
import { TreeViewSelectContext } from './TreeViewSelectContext';
import { NodeToggleCross } from './NodeToggle';
import { Box } from '@mui/material';

interface TreeFolderItemProps {
    item: TreeItem;
    isSelected: (item: TreeItem) => boolean;
    onNodeClick?: (item: TreeItem) => any;
    onToggle: (item: TreeItem) => any;
    noPadding?: boolean;
    horizontal?: boolean;
    firstChild?: boolean;
    lastChild?: boolean;
}

const TreeFolderItem: React.FC<TreeFolderItemProps> = ({ item, isSelected, onToggle, onNodeClick, noPadding, horizontal, firstChild, lastChild }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
            <TreeNodeItem
                item={item}
                isSelected={isSelected}
                onToggle={onToggle}
                onNodeClick={onNodeClick}
                firstChild={firstChild}
                lastChild={lastChild}
            />
            {Array.isArray(item.children) && item.children.length > 0 && item.toggled
                ? <div style={{ display: 'flex', flexDirection: 'row', width: '100%', minWidth: 0 }}>
                    <NodeToggleCross onlyColumn firstChild={lastChild} lastChild={lastChild} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {item.children.map((i, x, a) => (
                            <TreeFolderItem
                                key={`${x}_${i.id}`}
                                item={i}
                                isSelected={isSelected}
                                onToggle={onToggle}
                                onNodeClick={onNodeClick}
                                lastChild={x === a.length - 1}
                            />
                        ))}
                    </div>
                </div>
                : null}
        </div>
    )
}

interface TreeViewRootProps {
    tree: TreeItem[];
    onNodeClick?: (item: TreeItem) => any;
    isSelected: (item: TreeItem) => boolean;
    onToggle?: (item: TreeItem) => void;    
    noPadding?: boolean;
    horizontal?: boolean;
}

interface TreeViewRootState {
    tree: TreeItem[];
}

class TreeViewRoot extends React.Component<TreeViewRootProps, TreeViewRootState> {

    constructor(props: TreeViewRootProps) {
        super(props);
        this.state = {
            tree: props.tree
        }
        this.onItemToggle = this.onItemToggle.bind(this);
    }

    setStateAsync<K extends keyof TreeViewRootState>(state: Pick<TreeViewRootState, K>) {
        return new Promise<void>(resolve => this.setState(state, resolve));
    }

    mapTree(tree: TreeItem[], oldItem: TreeItem, newItem: TreeItem): TreeItem[] {
        return tree.map(i => i === oldItem
            ? newItem
            : Array.isArray(i.children)
                ? { ...i, children: this.mapTree(i.children, oldItem, newItem) }
                : i);
    }

    async replaceItem(oldItem: TreeItem, newItem: TreeItem) {
        const tree = this.mapTree(this.state.tree, oldItem, newItem);
        await this.setStateAsync({ tree });
    }

    onItemToggle(item: TreeItem) {
        const { onToggle } = this.props;
        const toggledItem: TreeItem = { ...item, toggled: !item.toggled };
        if (onToggle) onToggle(toggledItem);
        this.replaceItem(item, toggledItem);
    }

    render() {
        const { onNodeClick, isSelected, noPadding, horizontal } = this.props;
        const { tree } = this.state;
        return (
            <Box flex={1} display='flex' flexDirection='column' overflow='auto' width='100%' minWidth={0}>
                {tree.map((i, x, a) => (
                    <TreeFolderItem
                        key={`${x}_${i.id}`}
                        item={i}
                        isSelected={isSelected}
                        onToggle={this.onItemToggle}
                        onNodeClick={onNodeClick}
                        firstChild={x === 0}
                        lastChild={x === a.length - 1}
                        noPadding={noPadding}
                        horizontal={horizontal}
                    />
                ))}
            </Box>
        )
    }

}

interface TreeViewProps {
    tree: TreeItem[];
    onNodeClick?: (item: TreeItem) => any;
    onToggle?: (item: TreeItem) => void;
    noPadding?: boolean;
    defaultSelectedId?: string;
    horizontal?: boolean;
}

const TreeView: React.FC<TreeViewProps> = ({ tree, onNodeClick, noPadding, defaultSelectedId, horizontal, onToggle }) => {
    const ctx = useContext(TreeViewSelectContext);

    useEffect(() => {
        if (defaultSelectedId && !ctx.getItem()) {
            ctx.setTreeViewItem(defaultSelectedId);
        }
    }, []);

    function isSelected(item: TreeItem): boolean {
        return ctx.getItem() === item.id;
    }

    async function onClick(item: TreeItem) {
        if (item.id) {
            await ctx.setTreeViewItem(item.id);
        }
        onNodeClick?.call(null, item);
    }

    return (
        <TreeViewRoot
            tree={tree}
            isSelected={isSelected}
            onNodeClick={onClick}
            onToggle={onToggle}
            noPadding={noPadding}
            horizontal={horizontal}
        />
    );
}

export default TreeView;